import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { app } from '../../firebase'; 
import NavbarDashboard from '../Dashboard/NavbarDashboard';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import ResponsiveDrawer from '../Dashboard/Sidebar';

const firestore = getFirestore(app);

const TdsCredentials = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleStatusUpdate = async (id, value) => {
    try {
      const userRef = doc(firestore, 'tds_state', id);
      await updateDoc(userRef, { fileTds: value });
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === id ? { ...user, fileTds: value } : user
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating ITR File Status');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch users
        const usersSnapshot = await getDocs(collection(firestore, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch additional data
        const incomeSiteRegistrationSnapshot = await getDocs(collection(firestore, 'incomesite_registration'));
        const tdsFormSnapshot = await getDocs(collection(firestore, 'tds_forms'));
        const tdsStateSnapshot = await getDocs(collection(firestore, 'tds_state'));

        const incomeSiteData = incomeSiteRegistrationSnapshot.docs.map(doc => ({ userId: doc.id, ...doc.data() }));
        const tdsFormData = tdsFormSnapshot.docs.map(doc => ({ userId: doc.id, ...doc.data() ,  timestamp: doc.data().timestamp?.toDate() // Convert Firestore timestamp to JS Date
          }));
        const tdsStateData = tdsStateSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Merge usersData with additional data
        const mergedData = usersData.map(user => {
          const incomeSite = incomeSiteData.find(inc => inc.userId === user.id) || {};
          const tdsForm = tdsFormData.find(tdsp => tdsp.userId === user.id) || {};
          const tdsState = tdsStateData.find(tds => tds.id === user.id) || {};
          
          // Omit email/phone from tdsForm to prevent overwrite of registration credentials
          const { email: formEmail, phone: formPhone, ...restTdsForm } = tdsForm;
          
          return { 
            ...user, 
            ...incomeSite, 
            ...restTdsForm, 
            ...tdsState,
            formEmail: formEmail || '',
            formPhone: formPhone || ''
          };
        });

         // Sort data by timestamp, latest first
         const sortedData = mergedData.sort((a, b) => {
            const timestampA = a.timestamp ? new Date(a.timestamp) : new Date(0);
            const timestampB = b.timestamp ? new Date(b.timestamp) : new Date(0);
            return timestampB - timestampA;
          });

        setUsers(sortedData);
      } catch (error) {
        setError('Error fetching data');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.toLowerCase();

    return users.filter(
      user =>
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.pan?.toLowerCase().includes(query) ||
        (user.phone && user.phone.includes(searchTerm))
    );
  }, [users, searchTerm]);

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }} style={{marginTop:'5%'}}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <div className="container">
            <div className="search-bar">
              <TextField
                type="text"
                placeholder="Search by username, email, phone or PAN..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Button
                variant="contained"
                style={{ backgroundColor: 'lightgreen', marginLeft: '10px' }}
              >
                Search
              </Button>
            </div>

            {loading ? (
              <CircularProgress />
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="table-wrapper">
                <table className="responsive-table">
                  <thead className="sticky-header">
                    <tr>
                      <th>Sr No.</th>
                      <th>Date & time</th>
                      <th>User Name</th>
                      <th>Registration Email</th>
                      <th>Form Email</th>
                      <th>Registration Phone</th>
                      <th>Form Phone</th>
                      <th>PAN</th>
                      <th>Income Tax Site Password</th>
                      <th>ITR File Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => (
                      <tr key={user.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                        <td>{index + 1}</td>
                        <td>{user.timestamp ? new Date(user.timestamp).toLocaleString() : 'NA'}</td>
                        <td>{user.name || 'NA'}</td>
                        <td>{user.email || 'NA'}</td>
                        <td>{user.formEmail || 'NA'}</td>
                        <td>{user.phone || 'NA'}</td>
                        <td>{user.formPhone || 'NA'}</td>
                        <td>{user.pan || 'NA'}</td>
                        <td>{user.incometax_password || 'NA'}</td>
                        <td className={user.fileTds ? 'completed' : 'incompleted'}>
                          <select
                            value={user.fileTds || false}
                            onChange={(e) => handleStatusUpdate(user.id, e.target.value === 'true')}
                            style={{ backgroundColor: 'inherit', color: 'white', border: 'none', cursor: 'pointer', width: '100%', outline: 'none' }}
                          >
                            <option value={false} style={{ color: 'black' }}>Incomplete</option>
                            <option value={true} style={{ color: 'black' }}>Completed</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default TdsCredentials;
const css = `
/* Updated CSS for TdsCredentials component */

.incompleted {
  background-color: #ef0107;
  color: white;
}

.completed {
  background-color: #03c03c;
  color: white;
}

.even {
  background-color: #f2f2f2; /* Light gray background for even rows */
}

.odd {
  background-color: #ffffff; /* White background for odd rows */
}

.table-wrapper {
  overflow-x: auto; /* Enable horizontal scrolling */
  max-height: calc(100vh - 300px); /* Adjust based on your layout */
  position: relative;
}

.responsive-table {
  width: 100%;
  border-collapse: collapse;
}

.responsive-table th,
.responsive-table td {
  padding: 12px;
  text-align: left;
  white-space: nowrap;
  border: 1px solid #ddd;
}

.sticky-header th {
  position: sticky;
  top: 0;
  background-color: lightgreen;
  font-weight: bold;
  z-index: 2; /* Ensure header stays above table rows */
}

.sticky-header th {
  background-color: lightgreen; /* Header background color */
}

`;

document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);
